import { ExportHeaderIcon } from 'components/common/Icons';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RequestStatus } from 'utils/constants/RequestStatus';
import { useSignedUrl } from 'utils/hooks';

import { Button } from '@project44/manifest-ds';
import { Box, Flex, StyledSystem } from '@project44/manifest-ui-deprecated';
import {
  CircularProgress,
  makeStyles,
} from '@project44/material-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';
import { useSingleLogout, useUser } from '@project44/ui-utils';

import { getStatusMetaData, SHADOW } from './utils';

const useStyles = makeStyles({
  auxRoot: {
    position: 'absolute',
  },
  auxColor: {
    color: '#E9EAFA',
  },
  mainColor: {
    color: '#3442BC',
  },
});

interface CircularSpinnerProps {
  children: React.ReactNode;
  sx: StyledSystem.SystemStyleObject;
}

function CircularSpinner({ children, sx }: CircularSpinnerProps) {
  const classes = useStyles();
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', ...sx }}>
      <CircularProgress
        size={88}
        thickness={2}
        variant="determinate"
        value={100}
        classes={{ root: classes.auxRoot, colorPrimary: classes.auxColor }}
      />
      <CircularProgress
        size={88}
        thickness={2}
        classes={{ colorPrimary: classes.mainColor }}
        disableShrink
      />
      <Flex
        align="center"
        justify="center"
        sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
      >
        {children}
      </Flex>
    </Box>
  );
}

function ExportDashboard() {
  const { t, t__react } = useTranslation();
  const { logOutUser } = useSingleLogout();
  const user = useUser();
  const { data, status, exportType, doFetch } = useSignedUrl();

  useEffect(() => {
    doFetch();
  }, []);

  const {
    icon: Icon,
    title,
    description,
  } = getStatusMetaData({
    translateFn: t,
    translateReactFn: t__react,
    status,
    exportType,
  });

  return (
    <Flex
      data-testid="export-dashboard"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Flex
        align="center"
        sx={{
          width: '100%',
          height: '68px',
          boxShadow: SHADOW.medium,
          padding: 4,
        }}
      >
        <ExportHeaderIcon sx={{ marginRight: 6 }} />
        <Box
          as="h1"
          sx={{
            fontSize: 'medium',
            borderLeft: '1px solid',
            borderLeftColor: 'neutral.200',
            flex: 1,
            textAlign: 'left',
            paddingLeft: 6,
          }}
        >
          {t('global.export')}
        </Box>
      </Flex>
      <Flex sx={{ flex: 1 }} direction="column" align="center" justify="center">
        {status === RequestStatus.Loading ? (
          <CircularSpinner sx={{ marginBottom: 7 }}>
            <Icon />
          </CircularSpinner>
        ) : (
          <Icon sx={{ marginBottom: 7 }} />
        )}
        <Box as="p" sx={{ fontWeight: 'semibold', marginBottom: 2 }}>
          {title}
        </Box>
        <Box sx={{ fontSize: 'small', marginBottom: 4, maxWidth: '470px' }}>
          {typeof description === 'function'
            ? description(user.data?.username)
            : description}
        </Box>
        {status === RequestStatus.Success && (
          <a
            href={data}
            target="_self"
            style={{ textDecoration: 'none' }}
            download
          >
            <Button variant={exportType === 'async' ? 'primary' : 'secondary'}>
              {t('core.downloadNow')}
            </Button>
          </a>
        )}
        {[RequestStatus.Failure, RequestStatus.Timeout].includes(status) && (
          <Button variant="secondary" onPress={doFetch}>
            {t('global.tryAgain')}
          </Button>
        )}
        {status === RequestStatus.ExpirationError && (
          <Link to="/results" style={{ textDecoration: 'none' }}>
            <Button variant="secondary">{t('global.tryAgain')}</Button>
          </Link>
        )}
        {status === RequestStatus.PermissionError && (
          <Button variant="secondary" onPress={logOutUser}>
            {t('core.switchAccount')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
}

export default ExportDashboard;
